import { useContext, useEffect, useState } from "react";
import { FaBook } from "react-icons/fa";
import Cabecalho from "../../components/Cabecalho";
import Filtros from "../../components/Filtros";
import GridChamados from "../../components/GridChamados";
import Menu from "../../components/Menu";
import { AuthContext } from "../../context/auth";
import {
  DescEnum_TipoServicoChamado,
  SITUACOES_FALLBACK,
  SituacaoChamado,
  TipoServicoChamado,
  convertToArray,
  mapToIds,
} from "../../enums/enums";
import { api } from "../../services/apiClient";
import {
  descricaoDaSituacao,
  descricoesDeSituacoes,
  idsPorDescricoes,
  useSituacoes,
} from "../../services/situacoes";
import { handleError } from "../../services/erros/ErrorHandler";

import "./style.css";

export default function SolicitacaoCons() {
  const [loading, setLoading] = useState(false);
  const [chamados, setChamados] = useState([]);
  const [servicoProdutos, setServicoProdutos] = useState([]);
  const [servicoProdutoOptions, setServicoProdutoOptions] = useState([]);

  const { user } = useContext(AuthContext);

  // Situações vindas da tabela chamado_situacao — o filtro passa a listar o que existe no cadastro.
  const situacoes = useSituacoes();

  const clientesList =
    Array.isArray(user?.clientes) && user.clientes.length > 0
      ? user.clientes
      : user && user.cliente_id
        ? [{ id: user.cliente_id, label: user.cliente || "" }]
        : [];
  const clienteDisabled = clientesList.length === 1;

  // Inicializa já a partir do localStorage (quando existir) para não haver
  // corrida com o efeito abaixo que reage a filters.cliente_id: se o estado
  // partisse de cliente_id vazio e só fosse restaurado depois, esse efeito
  // rodaria uma vez com cliente_id vazio (limpando servico_produto) antes da
  // restauração "pegar", perdendo a preferência salva do usuário.
  const [filters, setFilters] = useState(() => {
    const defaults = {
      abertoDe: "01/07/2010",
      abertoAte: "31/12/2100",
      encerradoDe: "",
      encerradoAte: "",
      cliente_id: "",
      // Pré-seleção por id (regra), resolvida para a descrição que o filtro mostra e salva.
      situacaoChamado: [
        SituacaoChamado.Em_Aberto,
        SituacaoChamado.Aguardando_Aprovacao,
        SituacaoChamado.Em_Progresso,
      // Roda uma vez, antes de a lista real chegar da API — por isso resolve pelo fallback.
      ].map((id) => descricaoDaSituacao(SITUACOES_FALLBACK, id)),
      tipoChamado: [
        DescEnum_TipoServicoChamado[TipoServicoChamado.Duvida],
        DescEnum_TipoServicoChamado[TipoServicoChamado.Erro],
        DescEnum_TipoServicoChamado[TipoServicoChamado.Servico],
      ],
    };
    try {
      const saved = JSON.parse(localStorage.getItem("cmx_filters"));
      return saved ? { ...defaults, ...saved } : defaults;
    } catch (e) {
      return defaults;
    }
  });

  const situacoesChamado = descricoesDeSituacoes(situacoes);
  const tiposChamado = convertToArray(
    TipoServicoChamado,
    DescEnum_TipoServicoChamado,
  );

  useEffect(() => {
    carregarChamados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Saneia a seleção de situações guardada no localStorage contra a lista real do cadastro. É o que
  // faz a remoção de uma situação (o "Baixado", quando o Lizard sair) se resolver sozinha: a
  // descrição órfã sai do filtro salvo do usuário, sem erro e sem deploy. Se sobrar nada, volta para
  // a pré-seleção padrão — filtro vazio faz o back recusar a consulta ("Campos Obrigatórios").
  useEffect(() => {
    if (!Array.isArray(situacoes) || situacoes.length === 0) return;
    const validas = descricoesDeSituacoes(situacoes);
    setFilters((prev) => {
      const atuais = Array.isArray(prev.situacaoChamado) ? prev.situacaoChamado : [];
      const mantidas = atuais.filter((desc) => validas.includes(desc));
      if (mantidas.length === atuais.length) return prev;
      const novas =
        mantidas.length > 0
          ? mantidas
          : [
              SituacaoChamado.Em_Aberto,
              SituacaoChamado.Aguardando_Aprovacao,
              SituacaoChamado.Em_Progresso,
            ]
              .map((id) => descricaoDaSituacao(situacoes, id))
              .filter((desc) => validas.includes(desc));
      return { ...prev, situacaoChamado: novas };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [situacoes]);

  // Serviço/Produto depende do cliente selecionado no filtro (não mais do
  // cliente único do usuário).
  useEffect(() => {
    const init = async () => {
      try {
        const clienteId = +filters.cliente_id || 0;
        if (!clienteId) {
          setServicoProdutos([]);
          setServicoProdutoOptions([]);
          setFilters((prev) =>
            Array.isArray(prev.servico_produto) && prev.servico_produto.length === 0
              ? prev
              : { ...prev, servico_produto: [] },
          );
          return;
        }

        const resp = await api.get("/servico-produto/cons", {
          params: { cliente_id: clienteId },
        });
        const itens = resp.data || [];
        setServicoProdutos(itens);
        setServicoProdutoOptions(itens.map((i) => i.descricao));

        const atuais = Array.isArray(filters.servico_produto)
          ? filters.servico_produto
          : [];
        const validas = atuais.filter((d) =>
          itens.some((i) => i.descricao === d),
        );
        // Mantém a última seleção do usuário (preferência) se ainda válida
        // para o cliente selecionado; caso contrário, sugere todas as
        // opções disponíveis.
        const novas =
          validas.length > 0 ? validas : itens.map((i) => i.descricao);
        // Só atualiza `filters` (e reaciona o efeito de [filters], que busca os
        // chamados) quando o conjunto de fato mudou — senão toda troca de cliente
        // disparava CHAMADO/CONS duas vezes em paralelo (uma pela navegação, outra
        // por este setFilters "no-op"), sobrecarregando a mesma sessão no back.
        if (
          novas.length === atuais.length &&
          novas.every((d, i) => d === atuais[i])
        ) {
          return;
        }
        setFilters((prev) => ({ ...prev, servico_produto: novas }));
      } catch (e) {}
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.cliente_id]);

  async function carregarChamados() {
    const clienteId = +filters.cliente_id || 0;
    if (!clienteId) {
      setChamados([]);
      return;
    }

    setLoading(true);
    const {
      abertoDe,
      abertoAte,
      encerradoDe,
      encerradoAte,
      tipoChamado,
      situacaoChamado,
    } = filters;

    const situacaoNumeros = idsPorDescricoes(situacoes, situacaoChamado);
    const tipoServicoNumeros = mapToIds(
      TipoServicoChamado,
      DescEnum_TipoServicoChamado,
      tipoChamado,
    );

    const servicoProdutoIds = Array.isArray(filters.servico_produto)
      ? servicoProdutos
          .filter((sp) => filters.servico_produto.includes(sp.descricao))
          .map((sp) => sp.id)
      : [];

    const params = {
      cliente_id: clienteId,
      dataIni: abertoDe ? abertoDe : "01/07/2010",
      dataFim: abertoAte ? abertoAte : "31/12/2100",
      dataEncerramentoIni: encerradoDe || "",
      dataEncerramentoFim: encerradoAte || "",
      situacoes: JSON.stringify(situacaoNumeros),
      tipos: JSON.stringify(tipoServicoNumeros),
      servico_produto_ids: JSON.stringify(servicoProdutoIds),
    };

    const response = await api
      .get("/chamado/cons", { params })
      .catch((error) => {
        handleError(error, "Erro ao Consultar");
        setLoading(false);
      });

    if (response && response.data) {
      setChamados(response.data);
    } else {
      // toast.error("Erro ao carregar tickets");
    }
    setLoading(false);
  }

  return (
    <div>
      <Menu />
      <div className="content">
        <Cabecalho descricao={`Consulta de Solicitações`} titulo="Solicitações">
          <FaBook size={24} />
        </Cabecalho>

        <div className="content_solicitacao">
          <Filtros
            filters={filters}
            setFilters={setFilters}
            tiposChamado={tiposChamado}
            situacoesChamado={situacoesChamado}
            clienteOptions={clientesList}
            clienteDisabled={clienteDisabled}
            servicoProdutoOptions={servicoProdutoOptions}
          />
          <GridChamados chamados={chamados} />
        </div>
      </div>
    </div>
  );
}
